import { useMemo } from 'react';
import { useField } from 'formik';
import dayjs, { Dayjs } from 'dayjs';

import { useElementSize } from 'hooks';
import { getTimeData } from 'utils/date';
import { controlError } from 'utils/error';
import { joinStrings } from 'utils/helpers';
import { DatePicker } from 'components/date-picker';
import { TimePicker } from 'components/time-picker';
import { ErrorMessage } from 'components/error-message';

import * as Styles from './styles';
import { DateTimeFieldProps } from './types';

export const DateTimeField = ({
  name,
  disabled,
  timezone,
  timeLabel,
  dateLabel = 'ETA',
}: DateTimeFieldProps) => {
  const [field, meta, helpers] = useField(name);

  const error = controlError(
    { ...meta, touched: true },
    name,
    joinStrings([dateLabel, timeLabel], ' ')
  );

  const timezoneCode = timezone?.time_zone_code;

  const { date, time } = useMemo(() => {
    let dateValue = new Date();
    if (field.value) {
      if (timezoneCode) {
        const dateTZ = dayjs.utc(field.value).tz(timezoneCode);
        dateValue = dayjs(dateTZ.format('YYYY-MM-DD HH:mm:ss')).toDate();
      } else {
        dateValue = new Date(field.value);
      }
    }
    return {
      date: dateValue,
      time: getTimeData(dateValue),
    };
  }, [timezoneCode, field.value]);

  const setFieldValue = ({
    selectedDate,
    selectedTime,
  }: {
    selectedDate?: Dayjs;
    selectedTime?: number;
  }) => {
    const timeMs = selectedTime ?? time;
    let day = dayjs(selectedDate ?? field.value);
    if (timezoneCode) {
      day = day.tz(timezoneCode, !!selectedDate);
    }
    const startOfDay = day.startOf('d');

    helpers.setValue(startOfDay.add(timeMs, 'ms').toISOString());
  };

  const [sizeRef, { width }] = useElementSize();
  const isSmallScreen = useMemo(() => width < 300, [width]);
  const dateCol = isSmallScreen ? 12 : 6;
  const timeCol = isSmallScreen ? 12 : 6;

  return (
    <div ref={sizeRef}>
      {!timeLabel && (
        <Styles.Title disabled={disabled} className="semibold">
          {dateLabel}
        </Styles.Title>
      )}

      <Styles.StyledRow>
        <Styles.StyledCol col={dateCol} colTablet={dateCol}>
          <DatePicker
            selected={date}
            disabled={disabled}
            minDate={new Date()}
            label={timeLabel ? dateLabel : undefined}
            onChange={(value) => setFieldValue({ selectedDate: value })}
          />
        </Styles.StyledCol>
        <Styles.StyledCol col={timeCol} colTablet={timeCol}>
          <TimePicker
            value={time}
            label={timeLabel}
            isDisabled={disabled}
            setDefaultSelectedValue
            onChange={(value) => setFieldValue({ selectedTime: value })}
          />
        </Styles.StyledCol>
      </Styles.StyledRow>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

export type { DateTimeFieldProps };
